/**
 * Public (websales) checkout pricing — the single authority for "what does a
 * multi-passenger public checkout cost".
 *
 * Used by BOTH sides of the paid-checkout contract:
 *  - `client-public` (CheckoutPage/PaymentPage) computes the display total and
 *    the Stripe PaymentIntent amount from it.
 *  - `server/routes/public.ts` (batch booking endpoint) recomputes the total
 *    from authoritative route-segment + discount config and REJECTS the batch
 *    when the recomputed total does not equal `paymentIntent.amount`.
 *
 * Because the server treats submitted prices as display-only, any drift
 * between client math and server math would reject already-paid checkouts —
 * so the math lives here exactly once.
 *
 * Model (matches what booking rows have always recorded server-side):
 * every passenger in the batch shares one base fare (the segment fare for the
 * trip type) and the optional promo discount applies PER TICKET via
 * `calculateTicketPrice` — percent: round2(base × pct/100); fixed amount:
 * min(value, base). This is the same per-ticket semantics the admin counter
 * and the legacy per-passenger public endpoint persist.
 */
import {
  calculateTicketPrice,
  type DiscountDefinition,
  type TicketPricingResult,
} from './pricing-engine';

export interface PublicBatchPricing {
  /** Per-passenger pricing rows (all identical — one fare, one discount). */
  rows: TicketPricingResult[];
  /** Sum of per-ticket discountAmount across the batch (display value). */
  totalDiscount: number;
  /** Sum of per-ticket finalPrice across the batch. */
  total: number;
  /** `total` in integer cents — compare against Stripe `amount`. */
  totalCents: number;
}

/** Round a 2-dp currency value to integer cents without float drift. */
export function toCents(value: number): number {
  return Math.round(value * 100);
}

/**
 * Price a public multi-passenger checkout: one shared base fare, one optional
 * manual (promo-code) discount applied per ticket. Pure — throws only on a
 * negative base price (via calculateTicketPrice).
 */
export function computePublicBatchPricing(
  basePrice: number,
  passengerCount: number,
  discount: DiscountDefinition | null,
): PublicBatchPricing {
  if (!Number.isInteger(passengerCount) || passengerCount < 1) {
    throw new Error('passengerCount must be a positive integer');
  }

  const rows: TicketPricingResult[] = [];
  for (let i = 0; i < passengerCount; i++) {
    // NOTE: 'one-way' here is NOT a bug for round-trip checkouts. The
    // tripType parameter of calculateTicketPrice is documented-unused
    // (`_tripType` in pricing-engine.ts) — base-fare selection (one-way vs
    // round-trip fare) already happened UPSTREAM when the caller resolved
    // `basePrice` off the segment. Per-ticket discount math is identical
    // for both trip types, so any constant satisfies the signature.
    rows.push(calculateTicketPrice(basePrice, discount, 'one-way'));
  }

  // Sum in cents so a 2-dp per-ticket price never accumulates float error.
  const totalCents = rows.reduce((sum, r) => sum + toCents(r.finalPrice), 0);
  const totalDiscountCents = rows.reduce(
    (sum, r) => sum + toCents(r.discountAmount),
    0,
  );

  return {
    rows,
    totalDiscount: totalDiscountCents / 100,
    total: totalCents / 100,
    totalCents,
  };
}
